import { useState } from 'react';
import { useIsFetching, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FilterX } from 'lucide-react';
import { Botao, Selo, type CorSelo } from '../../componentes/base';
import { Carregando, Estado, Vazio } from '../../componentes/estados';
import { CampoData, Selecao, useDefsExtras } from '../../componentes/formulario';
import { Drawer } from '../../componentes/sobreposicao';
import { anunciarOcultos, useDiscreto } from '../../componentes/sensivel';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { get, getTodos } from '../../nucleo/api';
import { dataHora } from '../../nucleo/formato';
import { censurarCampo, jsonCensurado, nomesDoRegistro } from '../../nucleo/censura-json';
import { telaDaVisao } from '../../nucleo/nome-da-tela';
import { valorCru, rotuloAcao, rotuloCampo, rotuloEntidade, rotuloValor } from '../../nucleo/rotulos';
import type { DetalheAuditoria, LinhaDiff, RegistroAuditoria } from './tipos';

const COR_DIFF: Record<LinhaDiff['tipo'], CorSelo> = { adicionado: 'verde', removido: 'perigo', alterado: 'laranja' };

function Ator({ r }: { r: Pick<RegistroAuditoria, 'ator_tipo' | 'ator_nome' | 'ator_prefixo' | 'simulado_por' | 'simulado_por_nome'> }) {
  const { t } = useTranslation('admin');
  if (r.ator_tipo === 'sistema') return <>{t('auditoria.sistema')}</>;
  if (r.ator_tipo === 'chave_api') {
    // Nome e prefixo da chave identificam qual integração agiu.
    return (
      <span className="inline-flex min-w-0 flex-wrap items-baseline gap-x-1.5">
        <span className="text-tinta-3">{t('auditoria.chaveApi')}</span>
        {r.ator_nome && <span className="truncate" title={r.ator_nome}>{r.ator_nome}</span>}
        {r.ator_prefixo && <span className="numero text-xs text-tinta-3">{r.ator_prefixo}</span>}
      </span>
    );
  }
  // val4 (29/10): ação feita durante a simulação de um usuário de teste: a conta simulada e o ator real.
  if (r.simulado_por)
    return (
      <span className="inline-flex min-w-0 flex-wrap items-baseline gap-x-1.5" data-auditoria-simulado="">
        <span className="truncate" title={r.ator_nome ?? ''}>{r.ator_nome ?? ''}</span>
        <span className="text-xs text-tinta-3">{t('auditoria.simuladoPor', { nome: r.simulado_por_nome ?? '' })}</span>
      </span>
    );
  return <>{r.ator_nome ?? ''}</>;
}

/** M12.5: trilha de auditoria filtrada por usuário, entidade, ação e período, com detalhe antes e depois. */
export function Auditoria() {
  const { t } = useTranslation('admin');
  const [filtros, setFiltros] = useState({ usuario_id: '', entidade: '', acao: '', de: '', ate: '' });
  const [aberto, setAberto] = useState<RegistroAuditoria | null>(null);
  const membros = useQuery({ queryKey: ['/membros', 'auditoria'], queryFn: () => getTodos<{ id: string; nome: string }>('/membros'), staleTime: 60_000 });
  const opcoes = useQuery({ queryKey: ['/admin/auditoria-filtros'], queryFn: () => get<{ entidades: string[]; acoes: string[] }>('/admin/auditoria-filtros'), staleTime: 60_000 });
  const mudar = (k: keyof typeof filtros, v: string) => setFiltros((f) => ({ ...f, [k]: v }));
  const filtrado = Object.values(filtros).some(Boolean);

  const colunas: Coluna<RegistroAuditoria>[] = [
    { chave: 'created_at', rotulo: t('auditoria.data'), mono: true, render: (r) => dataHora(r.created_at) },
    { chave: 'ator_nome', rotulo: t('auditoria.usuario'), principal: true, render: (r) => <Ator r={r} /> },
    { chave: 'acao', rotulo: t('auditoria.acao'), render: (r) => <Selo cor="azul" quebra>{rotuloAcao(r.acao)}</Selo> },
    { chave: 'entidade', rotulo: t('auditoria.entidade'), render: (r) => rotuloEntidade(r.entidade) },
    { chave: 'entidade_id', rotulo: t('auditoria.registro'), mono: true, oculta: true, render: (r) => r.entidade_id?.slice(0, 8) ?? '' },
    { chave: 'ip', rotulo: t('auditoria.ip'), mono: true, oculta: true },
  ];

  return (
    <>
      <Tabela<RegistroAuditoria>
        tela="admin.auditoria"
        caminho="/admin/auditoria"
        params={{
          usuario_id: filtros.usuario_id || undefined,
          entidade: filtros.entidade || undefined,
          acao: filtros.acao || undefined,
          de: filtros.de || undefined,
          ate: filtros.ate || undefined,
        }}
        filtros={filtros}
        aoAplicarFiltros={(f) => setFiltros({ usuario_id: String(f.usuario_id), entidade: String(f.entidade), acao: String(f.acao), de: String(f.de), ate: String(f.ate) })}
        colunas={colunas}
        aoClicar={(r) => setAberto(r)}
        vazio={<Vazio texto={t('auditoria.vazio')} />}
        filtrosExtra={
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <div className="w-full sm:w-56">
              <Selecao aria-label={t('auditoria.usuario')} vazio={t('auditoria.todosUsuarios')} opcoes={(membros.data?.itens ?? []).map((m) => ({ valor: m.id, rotulo: m.nome }))} value={filtros.usuario_id} onChange={(e) => mudar('usuario_id', e.target.value)} />
            </div>
            <div className="w-full sm:w-56">
              <Selecao aria-label={t('auditoria.entidade')} vazio={t('auditoria.todasEntidades')} opcoes={(opcoes.data?.entidades ?? []).map((e) => ({ valor: e, rotulo: rotuloEntidade(e) }))} value={filtros.entidade} onChange={(e) => mudar('entidade', e.target.value)} />
            </div>
            <div className="w-full sm:w-56">
              <Selecao aria-label={t('auditoria.acao')} vazio={t('auditoria.todasAcoes')} opcoes={(opcoes.data?.acoes ?? []).map((a) => ({ valor: a, rotulo: rotuloAcao(a) }))} value={filtros.acao} onChange={(e) => mudar('acao', e.target.value)} />
            </div>
            <label className="flex items-center gap-1.5 text-sm text-tinta-3">
              {t('auditoria.de')}
              <CampoData type="date" className="w-36" value={filtros.de} max={filtros.ate || undefined} onChange={(e) => mudar('de', e.target.value)} />
            </label>
            <label className="flex items-center gap-1.5 text-sm text-tinta-3">
              {t('auditoria.ate')}
              <CampoData type="date" className="w-36" value={filtros.ate} min={filtros.de || undefined} onChange={(e) => mudar('ate', e.target.value)} />
            </label>
            {filtrado && (
              <Botao variante="fantasma" tamanho="sm" icone={<FilterX className="size-4" />} onClick={() => setFiltros({ usuario_id: '', entidade: '', acao: '', de: '', ate: '' })}>
                {t('auditoria.limpar')}
              </Botao>
            )}
          </div>
        }
      />
      {aberto && <Detalhe registro={aberto} aoFechar={() => setAberto(null)} />}
    </>
  );
}

/** Id do parceiro citado pelo registro (`parceiro_id`, `cliente_id`, `fornecedor_id`), no antes ou no depois. */
function parceiroDoRegistro(d: DetalheAuditoria | undefined): string | null {
  for (const r of [d?.depois, d?.antes]) {
    if (!r || typeof r !== 'object') continue;
    for (const k of ['parceiro_id', 'cliente_id', 'fornecedor_id']) {
      const v = (r as Record<string, unknown>)[k];
      if (typeof v === 'string' && v) return v;
    }
  }
  return null;
}

/** O registro da auditoria tem campos personalizados (`extras` no antes ou no depois, ou no diff)? */
function temExtras(d: DetalheAuditoria | undefined): boolean {
  if (!d) return false;
  const tem = (r: unknown) => !!r && typeof r === 'object' && 'extras' in r;
  return tem(d.antes) || tem(d.depois) || d.diff.some((l) => l.campo === 'extras' || l.campo.startsWith('extras.'));
}

function Detalhe({ registro, aoFechar }: { registro: RegistroAuditoria; aoFechar: () => void }) {
  const { t: tn } = useTranslation();
  return (
    <Drawer aberto aoFechar={aoFechar} largura="lg" titulo={`${rotuloAcao(registro.acao)} · ${rotuloEntidade(registro.entidade)}`}
      rodape={
        <Botao variante="fantasma" onClick={aoFechar}>
          {tn('comum.fechar')}
        </Botao>
      }
    >
      <ConteudoDetalhe registro={registro} />
    </Drawer>
  );
}

/** Conteúdo da gaveta do registro de auditoria (metadados, mudanças e JSON), com a censura do modo discreto. */
export function ConteudoDetalhe({ registro }: { registro: RegistroAuditoria }) {
  const { t } = useTranslation('admin');
  const { t: tn } = useTranslation();
  const q = useQuery({ queryKey: ['admin-auditoria-detalhe', registro.id], queryFn: () => get<DetalheAuditoria>(`/admin/auditoria/${registro.id}`) });
  // Review r11 visual (B1): com a opção de nomes, o nome do cliente também some de dentro do texto livre do registro (título da
  // oportunidade, observação), no diff e no JSON. Os nomes vêm do próprio registro e do parceiro que ele cita (sem permissão
  // de ler o parceiro, fica só o do registro).
  const { ativo, nomes: comNomes } = useDiscreto();
  const idParceiro = comNomes ? parceiroDoRegistro(q.data) : null;
  const parceiro = useQuery({ queryKey: ['admin-auditoria-parceiro', idParceiro], queryFn: () => get<{ nome: string }>(`/cadastros/parceiros/${idParceiro}`), enabled: !!idParceiro, retry: false, staleTime: 60_000 });
  const nomesDe = (d: DetalheAuditoria) => [...nomesDoRegistro(d.antes, d.entidade), ...nomesDoRegistro(d.depois, d.entidade), parceiro.data?.nome];
  // Review r15 spec M1: campo personalizado pela regra efetiva, com a definição (tipo, rótulo e a marca `sensivel`) da entidade.
  // Só quando o registro tem `extras`; sem a definição (sem permissão, campo removido), a regra usa o valor gravado.
  const defsExtras = useDefsExtras(temExtras(q.data) ? registro.entidade : undefined);
  // Review r16 spec M3 (as 6 agulhas da `discreto-plantado` na Auditoria): o nome do parceiro citado chega numa segunda
  // consulta, depois do detalhe; nesse intervalo o diff e o JSON apareciam com o nome por extenso (o cliente no nome e na
  // descrição do projeto). Com a opção de nomes, a gaveta espera essa consulta (e as definições dos campos personalizados com o
  // modo ligado) antes de mostrar o registro. Falha na leitura do parceiro (sem permissão) não segura: fica só o do registro.
  const camposCarregando = useIsFetching({ queryKey: ['campos', registro.entidade] }) > 0;
  const aguardando = (!!idParceiro && parceiro.isPending) || (ativo && temExtras(q.data) && camposCarregando);
  return (
    <Estado consulta={q}>
      {(d) =>
        aguardando ? (
          <Carregando />
        ) : (
          <div className="flex flex-col gap-5">
            <dl className="grid grid-cols-1 gap-x-4 gap-y-2 text-sm sm:grid-cols-2">
              <Meta rotulo={t('auditoria.data')} valor={dataHora(d.created_at)} mono />
              <Meta rotulo={t('auditoria.usuario')} valor={<Ator r={d} />} />
              <Meta rotulo={t('auditoria.registro')} valor={d.entidade_id ?? ''} mono />
              <Meta rotulo={t('auditoria.ip')} valor={d.ip ?? ''} mono />
              <Meta rotulo={t('auditoria.requisicao')} valor={d.request_id ?? ''} mono />
            </dl>

            {/* Permissão (r9 segurança M1), diferente do modo discreto: o servidor nem mandou essa parte. */}
            {d.ocultado && (
              <p role="note" data-auditoria-ocultado={d.ocultado} className="rounded-lg border border-laranja px-3 py-2 text-sm text-tinta-1">
                {t(d.ocultado === 'conteudo' ? 'auditoria.ocultadoConteudo' : 'auditoria.ocultadoCampos')}
              </p>
            )}
            <section>
              <h3 className="kicker mb-2">{t('auditoria.mudancas')}</h3>
              {d.diff.length === 0 ? (
                <p className="text-sm text-tinta-3">{t('auditoria.semMudancas')}</p>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-borda">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-borda text-left text-tinta-3">
                        <th scope="col" className="px-3 py-2 font-medium">{t('auditoria.campo')}</th>
                        <th scope="col" className="px-3 py-2 font-medium">{t('auditoria.antes')}</th>
                        <th scope="col" className="px-3 py-2 font-medium">{t('auditoria.depois')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.diff.map((l) => (
                        <tr key={l.campo} className="border-b border-borda align-top last:border-0">
                          <td className="px-3 py-2">
                            <span className="block break-words font-medium">{rotuloCampo(l.campo)}</span>
                            <Selo cor={COR_DIFF[l.tipo]}>{t(`auditoria.${l.tipo}`)}</Selo>
                          </td>
                          <td className={`px-3 py-2 break-words${!telaDaVisao(d.entidade, l.campo, l.de, tn) && valorCru(l.campo, l.de) ? ' numero' : ''} ${l.tipo === 'removido' || l.tipo === 'alterado' ? 'text-perigo' : 'text-tinta-3'}`}>{l.tipo === 'adicionado' ? '' : anunciarOcultos(telaDaVisao(d.entidade, l.campo, l.de, tn) ?? rotuloValor(l.campo, censurarCampo(l.campo, l.de, d.entidade, d.antes, nomesDe(d), defsExtras)))}</td>
                          <td className={`px-3 py-2 break-words${!telaDaVisao(d.entidade, l.campo, l.para, tn) && valorCru(l.campo, l.para) ? ' numero' : ''} ${l.tipo === 'adicionado' || l.tipo === 'alterado' ? 'text-verde' : 'text-tinta-3'}`}>{l.tipo === 'removido' ? '' : anunciarOcultos(telaDaVisao(d.entidade, l.campo, l.para, tn) ?? rotuloValor(l.campo, censurarCampo(l.campo, l.para, d.entidade, d.depois, nomesDe(d), defsExtras)))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <details className="rounded-lg border border-borda p-3 text-sm">
              <summary className="cursor-pointer text-tinta-2">{t('auditoria.verJson')}</summary>
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                <div className="min-w-0">
                  <p className="kicker mb-1">{t('auditoria.antes')}</p>
                  <pre className="numero overflow-x-auto rounded-md bg-sup-2 p-2 text-xs break-all whitespace-pre-wrap">{jsonCensurado(d.antes, d.entidade, nomesDe(d), defsExtras)}</pre>
                </div>
                <div className="min-w-0">
                  <p className="kicker mb-1">{t('auditoria.depois')}</p>
                  <pre className="numero overflow-x-auto rounded-md bg-sup-2 p-2 text-xs break-all whitespace-pre-wrap">{jsonCensurado(d.depois, d.entidade, nomesDe(d), defsExtras)}</pre>
                </div>
              </div>
            </details>
          </div>
        )
      }
    </Estado>
  );
}

function Meta({ rotulo, valor, mono }: { rotulo: string; valor: React.ReactNode; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-tinta-3">{rotulo}</dt>
      <dd className={`break-all ${mono ? 'numero' : ''}`}>{valor}</dd>
    </div>
  );
}
